// ============================================================================
// Qué se muestra según las banderas globales (épica #1219) — SÓLO presentación.
// ============================================================================
// Misma interfaz que el `useEntitlements()` de CC-CONSOLE (`ready`, `can`,
// `reasonFor`), para que EntitlementRoute, entitlementGates y
// filterByEntitlements sean copias idénticas. En WAYAK sólo existe el nivel 1:
//
//   • bandera `off`       → oculto para TODOS (incluidos los operadores);
//   • bandera `internal`  → sólo lo ven los operadores de banderas;
//   • un padre oculto oculta lo que cuelga de él, y una categoría sin apps
//     visibles desaparece (resolución de @torre/entitlements).
//
// Sin respuesta del backend NO se oculta nada. Una llave que el catálogo no
// conoce tampoco se oculta (no es gobernable).
// ============================================================================
import { useMemo } from "react";
import {
  menuKey,
  resolveEntitlements,
  type EntitlementKey,
  type Reason,
} from "@torre/entitlements";
// Catálogo v24: la vista de WAYAK. Lo que llegó de CC-CONSOLE sin código aquí no
// está en ella, así que (como antes) no se gobierna ni se oculta.
import { CATALOG, ENTITLEMENTS_TENANT } from "@/config/entitlementsCatalog";
import { useFeatureFlagsRuntimeStore, type PublicFlags } from "../store/featureFlagsRuntimeStore";
import { isFlagsOperatorRoleId, useSessionRoleId } from "./useIsFlagsOperator";

const KNOWN = new Set(CATALOG.map((n) => n.key));
const PARENT = new Map(CATALOG.map((n) => [n.key, n.parent]));

export interface FlagGate {
  /** `true` = ya hay respuesta del backend. */
  ready: boolean;
  /** ¿Se puede mostrar `key`? Sin datos, sí. */
  can: (key: EntitlementKey) => boolean;
  /** Por qué no se muestra `key` (o `null` si se muestra). */
  reasonFor: (key: EntitlementKey) => Reason | null;
}

/** Resolución pura (probada aparte): banderas + "¿es operador?". */
export function flagGateFor(data: PublicFlags | null, isOperator: boolean): FlagGate {
  if (!data) {
    return { ready: false, can: () => true, reasonFor: () => null };
  }
  const { keys, reasons } = resolveEntitlements({
    catalog: CATALOG,
    tenant: ENTITLEMENTS_TENANT,
    flags: data.restricted,
    license: "all",
    role: "all",
    // El operador ve lo `internal`; `off` le aplica igual que a todos.
    isPlatformAdmin: isOperator,
  });
  const granted = new Set(keys);
  const can = (key: EntitlementKey) => !KNOWN.has(key) || granted.has(key);
  const reasonFor = (key: EntitlementKey): Reason | null => {
    if (can(key)) return null;
    // "parent-denied" sube al motivo de origen: una app de Sistemas Internos no
    // está "apagada", es "sólo de la plataforma".
    let current: string | null = key;
    let reason: Reason = reasons[key] ?? "flag-off";
    for (let hops = 0; reason === "parent-denied" && current && hops < 10; hops += 1) {
      current = PARENT.get(current) ?? null;
      if (!current) break;
      reason = reasons[current] ?? reason;
    }
    return reason;
  };
  return { ready: true, can, reasonFor };
}

/** Banderas de la sesión y helpers (interfaz de CC-CONSOLE). */
export function useEntitlements(): FlagGate & { entitlements: PublicFlags | null } {
  const data = useFeatureFlagsRuntimeStore((s) => s.data);
  const isOperator = isFlagsOperatorRoleId(useSessionRoleId());
  return useMemo(() => ({ ...flagGateFor(data, isOperator), entitlements: data }), [data, isOperator]);
}

/** ¿Se puede mostrar `key`? Sin respuesta del backend, sí (nunca deja fuera). */
export function useEntitlement(key: string): boolean {
  return useEntitlements().can(key);
}

/**
 * Filtro de los ítems del menú interno de una app (`menu:<appId>.<id>`). Misma
 * firma que en CC-CONSOLE (#803), para que los layouts portados sean copias
 * idénticas. Devuelve `(itemId) => boolean` y se aplica ADEMÁS del preset. En
 * WAYAK sólo existe el nivel de banderas: un ítem que el catálogo no conoce no
 * se oculta, y sin respuesta del backend todo es visible.
 */
export function useMenuEntitlement(appId: string): (itemId: string) => boolean {
  const { can } = useEntitlements();
  return useMemo(() => (itemId: string) => can(menuKey(appId, itemId)), [can, appId]);
}
